<template>
  <viewOrderInvoice
      v-if="invoiceDialog"
      :dialog="invoiceDialog"
      :invoice-id="selectedInvoiceId"
      @update:dialog="invoiceDialog = $event"
  />
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <base-card
              :loading-allowed="false"
              :v-card-text-class="'pa-0'"
              icon="mdi-filter-variant"
              title="Sale Order Filter"
          >
            <v-form @submit.prevent="submit">
              <v-row dense class="pa-2">
                  <v-col cols="12" lg="3" md="2" sm="6">
                    <v-date-input
                      v-model="from_date.value.value"
                      :error-messages="from_date.errorMessage.value"
                      autocomplete="off"
                      bg-color="grey-lighten-4"
                      clearable
                      hide-details
                      density="compact"
                      input-format="dd/mm/yyyy"
                      label="From Date"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-month"
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" lg="3" md="2" sm="6">
                    <v-date-input
                      v-model="to_date.value.value"
                      :error-messages="to_date.errorMessage.value"
                      autocomplete="off"
                      bg-color="grey-lighten-4"
                      clearable
                      density="compact"
                      hide-details
                      input-format="dd/mm/yyyy"
                      label="To Date"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-month"
                      variant="outlined"
                    />
                  </v-col>
                </v-row>
              <v-divider></v-divider>
              <v-card-actions class="justify-end">
                <v-btn
                    ref="searchButton"
                    class="bg-brand"
                    prepend-icon="mdi-magnify"
                    type="submit"
                >
                  Search
                </v-btn>
              </v-card-actions>
            </v-form>
          </base-card>
        </v-col>

        <v-col cols="12">
          <BaseCard
              :loading-allowed="false"
              :v-card-text-class="'pa-0'"
              icon="mdi-clipboard-text-outline"
              title="Sale Order List"
          >
            <v-row align="center" class="py-2 px-1">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  append-inner-icon="mdi-magnify"
                  bg-color="grey-lighten-4"
                  clearable
                  density="compact"
                  hide-details
                  label="Search"
                  single-line
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="4">
                <div class="d-flex align-center flex-column">
                  <v-btn-group density="compact" divided size="small" variant="outlined">
                    <v-btn
                      color="success"
                      prepend-icon="mdi-download"
                      variant="flat"
                      @click="exportToCSV"
                    >
                      CSV
                    </v-btn>
                    <v-btn
                    color="primary"
                    prepend-icon="mdi-download"
                    variant="flat"
                   @click="exportToExcel"
                    >
                      EXCEL
                    </v-btn>
                    <v-btn
                      color="red"
                      prepend-icon="mdi-download"
                      variant="flat"
                      @click="exportToPDF"
                    >
                      PDF
                    </v-btn>
                  </v-btn-group>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-right pr-2">
                  <v-btn
                    v-if="$insertAccess($route.fullPath)"
                   color="primary"
                    prepend-icon="mdi-plus"
                    rounded="xl"
                    variant="tonal"
                    @click="goTo('/sales/add-sale-order')"
                  >
                    Create Sale Order
                  </v-btn>
                </div>
              </v-col>
            </v-row>

            <v-data-table
                v-model:items-per-page="itemsPerPage"
                :headers="headers"
                :hover="true"
                :items="getSchema"
                :loading="loading"
                :search="search"
                density="compact"
            >
              <template v-slot:loader>
                <v-progress-linear
                    :color="loading"
                    height="3"
                    indeterminate
                />
              </template>

              <template v-slot:item.index="{ index }">
                {{ index + 1 }}
              </template>

              <template v-slot:item.code="{ item }">
                <div class="text-left px-1">{{ item?.code }}</div>
              </template>

              <template v-slot:item.ref_no="{ item }">
                <div class="text-left px-1">{{ item?.ref_no }}</div>
              </template>

              <template v-slot:item.created_by.full_name="{ item }">
                <div class="text-left px-1">{{ item?.created_by?.full_name }}</div>
              </template>

              <template v-slot:item.updated_by.full_name="{ item }">
                <div class="text-left px-1">{{ item?.updated_by?.full_name }}</div>
              </template>

              <template v-slot:item.order_category="{ item }">
                {{ item?.order_category?.toUpperCase() }}
              </template>

              <template v-slot:item.employee.name="{ item }">
                <div class="text-left px-1">{{ item?.employee?.name }}</div>
              </template>

              <template v-slot:item.customer.company_name="{ item }">
                <div class="text-left px-1">{{ item?.customer?.company_name }}</div>
              </template>

              <template v-slot:item.sub_total="{ item }">
                <div class="text-end px-1">{{ item?.sub_total }}</div>
              </template>

              <template v-slot:item.discount_amount="{ item }">
                <div class="text-end px-1">{{ item?.discount_amount }}</div>
              </template>

              <template v-slot:item.vat_amount="{ item }">
                <div class="text-end px-1">{{ item?.vat_amount }}</div>
              </template>

              <template v-slot:item.tax_amount="{ item }">
                <div class="text-end px-1">{{ item?.tax_amount }}</div>
              </template>

              <template v-slot:item.total="{ item }">
                <div class="text-end px-1">{{ item?.total }}</div>
              </template>

              <template v-slot:item.paid="{ item }">
                <div class="text-end px-1">{{ item?.paid }}</div>
              </template>

              <template v-slot:item.due="{ item }">
                <div class="text-end px-1">{{ item?.due }}</div>
              </template>

              <template v-slot:item.conversion_status="{ item }">
                <v-chip
                    v-if="item.conversion_status"
                    size="small"
                >
                  {{ item.conversion_status }}
                </v-chip>
                <span v-else>
                  {{ "N/A" }}
                </span>
              </template>

              <template v-slot:item.approved_ind="{ item }">
                <v-chip
                    v-if="item.approved_ind == 1"
                    color="success"
                    size="small"
                    class="text-capitalize"
                >
                  Yes
                </v-chip>

                <v-chip
                    v-else
                    color="red"
                    size="small"
                    class="text-capitalize"
                >
                  No
                </v-chip>
              </template>

              <template v-slot:item.delivery_status="{ item }">
                <v-chip
                    :color="item.delivery_status == 'pending' ? '#FFD600' : (item.delivery_status == 'partial' ? 'orange' : 'green')"
                    size="small"
                    class="text-capitalize"
                >
                  {{ item.delivery_status }}
                </v-chip>
              </template>

              <template v-slot:item.approval_status_display="{ item }">
                <v-chip
                    v-if="item.approval_current_process && item.approval_current_process.status === 'Pending'"
                    color="yellow-darken-3"
                    size="small"
                >
                  Wating for {{ item.approval_current_process.approval_step_name.name }}
                </v-chip>

                <v-chip
                    v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                    color="green"
                    size="small"
                >
                  Approved By {{ item.approval_current_process.approval_step_name.name }}
                </v-chip>

                <v-chip
                    v-else-if="item.approval_current_process && item.approval_current_process.status === 'Cancelled'"
                    color="red"
                    size="small"
                >
                  Cancelled By {{ item.approval_current_process.approval_step_name.name }}
                </v-chip>

                <v-chip
                    v-else
                    color="green"
                    size="small"
                >
                  {{ item.approval_status }}
                </v-chip>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                    :color="statusMap[item.status]?.color || 'grey'"
                    size="small"
                    class="text-capitalize"
                >
                  {{ statusMap[item.status]?.label || "Unknown" }}
                </v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-icon
                    small
                    @click="viewOrderInvoice(item)"
                    v-if="$viewAccess($route.fullPath)"
                    class="me-2 edit-button"
                    title="View Invoice"
                    color="blue"
                >
                  mdi-file-document-outline
                </v-icon>

                <v-icon
                    class="me-2 edit-button"
                    color="primary"
                    @click="editItem(item)"
                    v-if="$updateAccess($route.fullPath) && item.lock == '0' && canEdit(item)"
                >
                  mdi mdi-pencil-outline
                </v-icon>
              </template>
            </v-data-table>
          </BaseCard>
        </v-col>
      </v-row>
    </v-container>
  </v-app>

</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import { toast } from "vue3-toastify";
import { formatDateView } from "@/plugins/helpers";
import formattedDate from "@/plugins/dateFormatter";
import BaseCard from "@/components/utils/baseCard.vue";
import viewOrderInvoice from "@/components/module/sales/orderManagement/viewOrderInvoice.vue";

import statusMap from "@/utils/statusMap";

export default {
        components: {
          BaseCard,
          viewOrderInvoice
        },
        data: () => ({
          itemsPerPage: 10,
          loading: false,
          search: "",
          dialog: false,
          invoiceDialog: false,
          selectedInvoiceId: null,
          data: {},
          statusMap,
          headers: [
            { title: "#SL", align: "center", key: "index", width: "15px", sortable: false },
            { title: "Order No", key: "code",align: "center", width: "100px", sortable: false },
            { title: "Ref No", key: "ref_no",align: "center", width: "60px", sortable: false },
            { title: "Created By", key: "created_by.full_name",align: "center", width: "60px", sortable: false },
            { title: "Updated By", key: "updated_by.full_name",align: "center", width: "60px", sortable: false },
            { title: "Sales Person", key: "employee.name",align: "center", width: "60px", sortable: false },
            { title: "Work Order", key: "work_order",align: "center", width: "100px", sortable: false },
            { title: "Sale Order Date", key: "sale_order_date",align: "center", width: "120px", sortable: false },
            { title: "Order Category", key: "order_category",align: "center", width: "120px", sortable: false },
            { title: "Shipping Date", key: "shipping_date",align: "center" , width: "120px", sortable: false },
            { title: "Apprx. Delivery Date", key: "approximate_delivery_date",align: "center", width: "120px", sortable: false },
            { title: "Customer", key: "customer.company_name",align: "center" , width: "150px", sortable: false },
            { title: "Sub Total", key: "sub_total",align: "center" , width: "60px", sortable: false },
            { title: "Discount", key: "discount_amount", align: "center" , width: "60px", sortable: false },
            { title: "Vat", key: "vat_amount",align: "center" , width: "60px", sortable: false },
            { title: "Tax", key: "tax_amount",align: "center" , width: "60px", sortable: false },
            { title: "Total Amount",align: "center", key: "total", width: "80px", sortable: true },
            { title: "Advance Payment", align: "center", key: "paid", sortable: true },
            { title: "Due", align: "center", key: "due", sortable: true },
            { title: "Order Type", align: "center", key: "order_category", sortable: true },
            { title: "Next Payment Date", align: "center", key: "next_payment_date", sortable: true },
            { title: "Coversion Status", align: "center", key: "conversion_status", sortable: true },
            { title: "Approval Status", align: "center", key: "approval_status_display", sortable: true },
            { title: "Status", align: "center", key: "status", sortable: true },
            { title: "Actions", align: "center", key: "actions", width: "150px", sortable: false }
          ],
        }),
        watch: {
        },
        created(){
            // if(this.$store.getters.getProjectList.length === 0) {this.$store.dispatch('getProjectList');}
        },
        computed:{
            getSchema() {
              return this.$store.getters.getSaleOrderList;
            },
            getProjectList() {
              // return this.$store.getters.getProjectList;
              return [];
            },
        },
        setup() {
          const { handleSubmit, resetForm} = useForm({
            validationSchema: yup.object({
                // from_date: yup.date().required().label('From Date'),
                // to_date: yup.date().required().label('To Date'),
            }),
          });

        const customer_id = useField("customer_id");
        const from_date = useField("from_date");
        const to_date = useField("to_date");
        const errorMessages = ref("");
        const loading = ref("");
        const store = useStore();
        const submit = handleSubmit((values) => {
          loading.value = "blue-darken-3";
          values.from_date = formattedDate(from_date.value.value);
          values.to_date = formattedDate(to_date.value.value);

          store
            .dispatch("getSaleOrderList", values)
            .then((response) => {
              loading.value = false;
            })
            .catch((error) => {
              toast.error(error.response.data.message);
            });
        });
    return {
      submit,
      from_date,
      to_date,
      errorMessages,
      customer_id,
      loading,
    };
  },
  methods: {
    formatDateView,
    goTo(val) {
      this.$router.push({ path: val }).catch((err) => {});
    },
    editItem(item) {
      // this.$store.commit('setEditingRfq', item);
      this.$router.push({
        name: "add-sale-order",
        query: { id: item.id },
      });
      // this.data = { ...item };
      // this.dialog = true;
    },
     canEdit(item){
      return (!item.approval_status || item.approval_status.toLowerCase() == 'pending' || item.status == 0)
    },
    isApproved(item) {
      // Check if approval status is "Approved"
      if (item.approval_current_process && item.approval_current_process.status === 'Approved') {
        return true;
      }
      // Fallback to check approval_status field
      if (item.approval_status && item.approval_status.toLowerCase() === 'approved') {
        return true;
      }
      return false;
    },
    createSaleInvoice(item) {
      this.$router.push({
        name: "add-sale-invoice",
        query: { order_id: item.id }
      })
    },
    viewOrderInvoice(item) {
      this.selectedInvoiceId = item.id;
      this.invoiceDialog = true;
    },
    exportToPDF() {
      let serialNumber = 1;
      const columns = [ "#SL", "Order No", "Ref No", "Created By", "Updated By", "Sales Person", "Work Order", "Sale Order Date", "Shipping Date", "Apprx. Delivery Date", "Customer", "Sub Total", "Discount", "Vat", "Tax", "Total Amount", "Advance Payment", "Due", "Order Type", "Next Payment Date", "Conversion Status", "Approval Status", "Status" ];
      const rows = this.getSchema.map((item) => [
        serialNumber++,
        item.code,
        item.ref_no,
        item.created_by?.full_name || "-",
        item.updated_by?.full_name || "-",
        item.employee?.name || "-",
        item.work_order?.code || "-",
        this.formatDateView(item.sale_order_date),
        this.formatDateView(item.shipping_date),
        this.formatDateView(item.approximate_delivery_date),
        item.customer?.company_name || "-",
        item.sub_total || 0,
        item.discount_amount || 0,
        item.vat_amount || 0,
        item.tax_amount || 0,
        item.total || 0,
        item.paid || 0,
        item.due || 0,
        item.delivery_status || "-",
        this.formatDateView(item.next_payment_date),
        item.conversion_status || "-",
        item.approval_status || "-",
        this.statusMap[item.status]?.label || "Unknown",
      ]);
      this.$exportTableToPDF(
        columns,
        rows,
        "Sale Order List",
        "sale_order_record_" + this.$fileDate(),
        "landscape"
      );
    },
    exportToExcel() {
      var data = [
        [
          "#SL",
          "Order No",
          "Ref No",
          "Created By",
          "Updated By",
          "Sales Person",
          "Work Order",
          "Sale Order Date",
          "Shipping Date",
          "Apprx. Delivery Date",
          "Customer",
          "Sub Total",
          "Discount",
          "Vat",
          "Tax",
          "Total Amount",
          "Advance Payment",
          "Due",
          "Order Type",
          "Next Payment Date",
          "Conversion Status",
          "Approval Status",
          "Status",
        ],
      ];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          item.ref_no,
          item.created_by?.full_name || "-",
          item.updated_by?.full_name || "-",
          item.employee?.name || "-",
          item.work_order?.code || "-",
          this.formatDateView(item.sale_order_date),
          this.formatDateView(item.shipping_date),
          this.formatDateView(item.approximate_delivery_date),
          item.customer?.company_name || "-",
          item.sub_total || 0,
          item.discount_amount || 0,
          item.vat_amount || 0,
          item.tax_amount || 0,
          item.total || 0,
          item.paid || 0,
          item.due || 0,
          item.delivery_status || "-",
          this.formatDateView(item.next_payment_date),
          item.conversion_status || "-",
          item.approval_status || "-",
          this.statusMap[item.status]?.label || "Unknown",
        ]);
      });
      this.$exportTableToExcel(data, "sale_order_record_" + this.$fileDate());
    },
    exportToCSV() {
      var data = [
        [
          "#SL",
          "Order No",
          "Ref No",
          "Created By",
          "Updated By",
          "Sales Person",
          "Work Order",
          "Sale Order Date",
          "Shipping Date",
          "Apprx. Delivery Date",
          "Customer",
          "Sub Total",
          "Discount",
          "Vat",
          "Tax",
          "Total Amount",
          "Advance Payment",
          "Due",
          "Order Type",
          "Next Payment Date",
          "Conversion Status",
          "Approval Status",
          "Status",
        ],
      ];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          item.ref_no,
          item.created_by?.full_name || "-",
          item.updated_by?.full_name || "-",
          item.employee?.name || "-",
          item.work_order?.code || "-",
          this.formatDateView(item.sale_order_date),
          this.formatDateView(item.shipping_date),
          this.formatDateView(item.approximate_delivery_date),
          item.customer?.company_name || "-",
          item.sub_total || 0,
          item.discount_amount || 0,
          item.vat_amount || 0,
          item.tax_amount || 0,
          item.total || 0,
          item.paid || 0,
          item.due || 0,
          item.delivery_status || "-",
          this.formatDateView(item.next_payment_date),
          item.conversion_status || "-",
          item.approval_status || "-",
          this.statusMap[item.status]?.label || "Unknown",
        ]);
      });

      this.$exportTableToCSV(data, "sale_order_record_" + this.$fileDate());
    },
  },
  mounted() {
    // const today = new Date();
    // const oneMonthBack = new Date();
    // oneMonthBack.setMonth(today.getMonth() - 1);
    // this.from_date.value.value = oneMonthBack;
    // this.to_date.value.value = today;

    this.$nextTick(() => {
      this.$refs.searchButton.$el.click();
    });

  },
};
</script>
